import { submitAppRecommendation } from '$tablet-app/src/app/app_library/api/mutations';
import filterAppsOnAppScreen from '$tablet-app/src/app/app_library/functions/filterAppsOnAppScreen';
import openApp from '$tablet-app/src/app/app_library/functions/openApp';
import openAppInGooglePlay from '$tablet-app/src/app/app_library/functions/openAppInGooglePlay';
import { AppRecord } from '$tablet-app/src/app/app_library/types';
import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { postAppOpenLogEvent } from '$tablet-app/src/app/logging';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import { useAuthUsername } from '@akin-home/auth/react';
import { showSuccessToast } from '@akin-home/toast/native';
import React, { useCallback, useEffect } from 'react';
import { useIsFetching, useQueryClient } from 'react-query';
import { useAvailableApps } from '../../hooks';
import SuggestAppDialog from '../SuggestAppDialog';
import AppSelectionScreenView from './AppSelectionScreenView';
import { useAppSelectionScreenReducer } from './reducer';

const AppSelectionScreen = () => {
  const [state, dispatch] = useAppSelectionScreenReducer();
  const user_id = useCurrentUserID();
  const username = useAuthUsername() || '';
  const client = useQueryClient();
  const is_focused = useIsFetching();

  const { mode, search_text, suggest_app_dialog_visible, category_filters } =
    state;

  useUpdateScreenChatContext();

  const toggleSuggestAppDialog = () =>
    dispatch({ type: 'toggle_request_app_dialog' });

  const { apps, categories, isLoading, isRefetching, refetch } =
    useAvailableApps();

  const filtered_apps = filterAppsOnAppScreen({
    apps,
    text: search_text,
    categories: category_filters,
  });

  const onPressApp = useCallback(
    async (app: AppRecord) => {
      const res = await openApp(app);

      sendBackgroundMessage(intent_labels.open_app, {
        context: { app: { name: app.label, package_name: app.package_name } },
      });

      /** Log the app being opened */
      postAppOpenLogEvent({
        user_id,
        data: {
          package_name: app.package_name,
        },
      });

      if (res.error) {
        console.log('Failed to open: ', app.label);
      }
    },
    [user_id]
  );

  const onPressInstallApp = () =>
    openApp({
      label: 'Google Play',
      package_name: 'com.android.vending',
    });

  const onConfirmSuggestApp = (app_name: string) => {
    submitAppRecommendation({
      app_name,
      username,
    });

    toggleSuggestAppDialog();

    showSuccessToast({
      text1: 'Thank you!',
      text2: 'We will review your submission shortly',
    });
  };

  const reload = () => {
    client.invalidateQueries('apps');
    refetch();
  };

  const setSearchText = (search_text: string) =>
    dispatch({ type: 'set_search_text', payload: { search_text } });

  const onPressCategoryFilter = (category?: string) =>
    dispatch({ type: 'toggle_category_filter', payload: { category } });

  useEffect(() => {
    if (is_focused) {
      sendBackgroundMessage(intent_labels.open_apps_screen);
    }
  }, [is_focused]);

  return (
    <>
      <AppSelectionScreenView
        apps={filtered_apps}
        mode={mode}
        isRefetching={isRefetching}
        isLoading={isLoading}
        categories={categories}
        search_text={search_text}
        category_filters={category_filters}
        onChangeSearchText={setSearchText}
        onPressLibraryApp={openAppInGooglePlay}
        refresh={reload}
        onPressApp={onPressApp}
        onPressCategoryFilter={onPressCategoryFilter}
        onPressSuggestAnApp={toggleSuggestAppDialog}
        onPressInstallApp={onPressInstallApp}
        onPressUninstall={openAppInGooglePlay}
      />

      <SuggestAppDialog
        visible={suggest_app_dialog_visible}
        onClose={toggleSuggestAppDialog}
        onDone={onConfirmSuggestApp}
      />

      <DeveloperScreenInfo />
    </>
  );
};

export default AppSelectionScreen;
